import { MoreVertical, MapPin, FileText, CheckSquare, DollarSign, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface JobHeaderProps {
  job: {
    job_number: string;
    owner_name: string;
    property_address: string;
    property_type: string;
    status: string;
    created_at: string;
    updated_at?: string;
    estimated_value?: number;
  };
  photoCount?: number;
  documentCount?: number;
  taskCount?: number;
  onEditJob: () => void;
  onDuplicateJob?: () => void;
  onArchiveJob?: () => void;
  onTabChange?: (tab: string) => void;
  onOpenDocuments?: () => void;
  onOpenTasks?: () => void;
  onOpenPayments?: () => void;
  onOpenTimeline?: () => void;
}

export const JobHeader = ({
  job,
  photoCount = 0,
  documentCount = 0,
  taskCount = 0,
  onEditJob,
  onDuplicateJob,
  onArchiveJob,
  onTabChange,
  onOpenDocuments,
  onOpenTasks,
  onOpenPayments,
  onOpenTimeline,
}: JobHeaderProps) => {
  const formatRelativeDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div className="relative bg-gradient-to-br from-card via-card to-card/90 border-b border-primary/20 glow-border">
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-secondary/5 pointer-events-none" />
      <div className="container mx-auto px-6 py-4 relative">
        <div className="flex items-start justify-between gap-6">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <Badge variant="outline" className="text-xs font-display font-semibold border-primary/30 text-primary bg-primary/10 px-3 py-0.5 premium-glow">
                {job.job_number}
              </Badge>
              <Badge 
                variant="outline" 
                className={`text-xs font-display font-semibold px-3 py-0.5 premium-glow ${
                  job.property_type?.toLowerCase() === 'insurance' 
                    ? 'border-blue-500/30 text-blue-600 bg-blue-500/10 dark:text-blue-400' 
                    : job.property_type?.toLowerCase() === 'commercial'
                    ? 'border-orange-500/30 text-orange-600 bg-orange-500/10 dark:text-orange-400'
                    : 'border-green-500/30 text-green-600 bg-green-500/10 dark:text-green-400'
                }`}
              >
                {job.property_type?.toLowerCase() === 'insurance' 
                  ? '🏢 Insurance' 
                  : job.property_type?.toLowerCase() === 'commercial'
                  ? '🏢 Commercial'
                  : '🏠 Retail'}
              </Badge>
              <Badge variant="outline" className="text-xs font-display border-primary/30 text-primary bg-primary/10 premium-glow">
                {job.status}
              </Badge>
            </div>
            <h1 className="text-3xl font-display font-bold text-gradient mb-1">
              {job.owner_name}
            </h1>
            
            {/* Quick Stats Row */}
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-primary" />
                <span className="text-foreground font-medium">{job.property_address}</span>
              </span>
              {job.estimated_value && job.estimated_value > 0 && (
                <>
                  <span className="text-primary">•</span>
                  <span>
                    Est. Value: <span className="text-foreground font-medium">${job.estimated_value.toLocaleString()}</span>
                  </span>
                </>
              )}
              {photoCount > 0 && (
                <>
                  <span className="text-primary">•</span>
                  <span>
                    <span className="text-foreground font-medium">{photoCount}</span> {photoCount === 1 ? 'photo' : 'photos'}
                  </span>
                </>
              )}
              {documentCount > 0 && (
                <>
                  <span className="text-primary">•</span>
                  <span>
                    <span className="text-foreground font-medium">{documentCount}</span> {documentCount === 1 ? 'doc' : 'docs'}
                  </span>
                </>
              )}
              {taskCount > 0 && (
                <>
                  <span className="text-primary">•</span>
                  <span>
                    <span className="text-foreground font-medium">{taskCount}</span> {taskCount === 1 ? 'task' : 'tasks'}
                  </span>
                </>
              )}
              <span className="text-primary">•</span>
              <span>
                {new Date(job.created_at).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <TooltipProvider>
                {/* Documents */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-9 w-9 premium-glow hover:bg-primary/10 hover:text-primary"
                      onClick={onOpenDocuments}
                    >
                      <FileText className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Documents</p>
                  </TooltipContent>
                </Tooltip>

                {/* Tasks */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-9 w-9 premium-glow hover:bg-primary/10 hover:text-primary"
                      onClick={onOpenTasks}
                    >
                      <CheckSquare className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Tasks</p>
                  </TooltipContent>
                </Tooltip>

                {/* Payments */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-9 w-9 premium-glow hover:bg-primary/10 hover:text-primary"
                      onClick={onOpenPayments}
                    >
                      <DollarSign className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Payments</p>
                  </TooltipContent>
                </Tooltip>

                {/* Timeline */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-9 w-9 premium-glow hover:bg-primary/10 hover:text-primary"
                      onClick={onOpenTimeline}
                    >
                      <Clock className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Timeline</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9 ml-1 premium-glow hover:bg-primary/10 hover:text-primary">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="premium-card border-primary/20">
                  <DropdownMenuItem onClick={onEditJob}>Edit Job</DropdownMenuItem>
                  <DropdownMenuItem onClick={onDuplicateJob}>
                    Duplicate Job
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onArchiveJob}>
                    Archive Job
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            {job.updated_at && (
              <p className="text-xs text-muted-foreground mt-1">
                Last contacted: <span className="text-foreground font-medium">{formatRelativeDate(job.updated_at)}</span>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};